import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from '@react-router';
import { CardPreviewPane } from './components/CardCustomizationContent/CardPreviewPane';
import { hasSuiteImage } from '@apps/card/utils/suiteImage';
import type { SimpleCardPdpModalProps as ProductDetailsPageProps, StationeryProductSelector, StationeryTemplatePDP } from './ProductDetailsPage.types';
import { enrichCardTelemetryExtraInfo, useCardTelemetry } from '@apps/card/Card.telemetry';
import { CardFormat } from './constants';
import { useStickyCustomizeButton } from './useStickyCustomizeButton';
import { useIsMobileOrTablet } from '@shared/utils/media/useMediaScreens';
import type { StationeryTemplateCategoryEnum } from '@graphql/generated-base';
import { useColorways } from '@apps/card/hooks/useColorways';

import type { DigitalDesignTemplate } from './usePdpData';
import { usePdpData } from './usePdpData';
import { CardCustomizationPane } from './components/CardCustomizationContent/CardCustomizationPane';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import type { ColorwayOption, DraftData } from '@apps/card/routes/CardCustomizer/CardCustomizer.types';
import { DigitalCustomizationPane } from './components/DigitalCustomizationContent/DigitalCustomizationPane';
import { DigitalPreviewPane } from './components/DigitalCustomizationContent/DigitalPreviewPane';
import { templateToTitle } from '@apps/card/utils';
import { PdpLayout } from './components/PdpLayout/PdpLayout';
import { usePriceSummary } from '@apps/card/components/PriceSummary/usePriceSummary';
import { SkeletonGroup } from '@shared/components/Skeleton';
import { useCustomizeClickHandler } from './useCustomizeClickHandler';
import type { Format, MinimalRequiredTemplateDataForDraft } from '../DesignsGallery/DesignsGallery.types';
import { useCardCategoryContext } from '../CardCategoryProvider';
import { PremiumDigitalPdp } from './PremiumDigitalPdp';
import { isFoilTemplate } from '../DesignsGallery/utils';
import { wrapWithStationeryProductDetailsHealthProvider } from './ProductDetails.health';
import { PdpSeo } from './PdpSeo';
import { StationeryProductPricingContextProvider, useStationeryProductPricingContext } from '@apps/card/components/ProductUIConfig/utils/useStationeryProductPricingContext';
import { withWindow } from '@shared/utils/withWindow';
import { useQueryParamHelper } from '@shared/core/queryString';
import { useFreeShippingCountries } from './useFreeShippingCountries';
import { useFreeShippingBanner } from '@apps/card/hooks/useFreeShippingBanner';
import { FREE_DIGITAL_DUMMY_PRODUCT } from '@apps/card/components/ProductUIConfig/freeDigitalProduct';

interface RootProps {
  eventId?: string;
  // Null for digital-only themes: no printable paper template exists (e.g. its
  // StationeryTemplate is disabled), only a Joy digital design. Rendered as a
  // free basic-digital PDP.
  template: StationeryTemplatePDP | null;
  digitalDesignTemplate: DigitalDesignTemplate | null;
  templateCategory: StationeryTemplateCategoryEnum;
  themeId: string;
  designName: string;
  onCustomizeCardClickOverride?: ProductDetailsPageProps['onCustomizeCardClickOverride'];
}

export interface BasePdpImplementationProps {
  eventId?: string;
  themeId: string;
  templateCategory: StationeryTemplateCategoryEnum;
  isCreatingDraft: boolean;
  // cardFormatControl: CardFormatControl;
  productSelector: StationeryProductSelector;
  /**
   * Should be applied to the element that will determine when to show
   * the customize button now in the header.
   */
  intersectionRef: React.RefObject<HTMLButtonElement>;
  showCustomizeButtonInHeader: boolean;
  hideCustomizeButtonInCustomizationPane: boolean;
  designName: string;
  isFoil: boolean;
}

/**
 * Colorway state shared by every format that carries colorways (paper +
 * premium digital). Resolved once in `Root` via `useColorways` and threaded
 * down so selection is a single source of truth across format switches.
 */
export interface ColorwayControlProps {
  options?: ReadonlyArray<ColorwayOption>;
  selectedColorwayId: string;
  onColorwaySelect: (id: string) => void;
  cssVarStyle: React.CSSProperties;
  preloadImages: () => void;
}

/**
 * Bakes the selected colorway into the template so the created draft opens on
 * the chosen variant. Returns the template unchanged when there's no matching
 * colorway.
 */
const withSelectedColorway = <T extends MinimalRequiredTemplateDataForDraft>(template: T, options: ReadonlyArray<ColorwayOption> | undefined, selectedColorwayId: string): T => {
  const hasColorway = !!(options && selectedColorwayId && options.some(o => o.id === selectedColorwayId));
  if (!hasColorway) {
    return template;
  }
  return {
    ...template,
    themeJSON: {
      ...template.themeJSON,
      card: {
        ...template.themeJSON.card,
        front: {
          ...template.themeJSON.card.front,
          colorways: { ...template.themeJSON.card.front.colorways!, selectedId: selectedColorwayId },
        },
      },
    },
  };
};

interface PaperPdpProps extends BasePdpImplementationProps, ColorwayControlProps {
  template: StationeryTemplatePDP;
  onPaperCustomization: (draft: DraftData) => void;
  onCustomizeClick: (template: MinimalRequiredTemplateDataForDraft, subtotalInMinorUnits?: number) => void;
}

const PaperPdp = (props: PaperPdpProps) => {
  const {
    productSelector,
    intersectionRef,
    isCreatingDraft,
    onCustomizeClick,
    onPaperCustomization,
    showCustomizeButtonInHeader,
    template,
    themeId,
    designName,
    templateCategory,
    eventId,
    isFoil,
    options,
    selectedColorwayId,
    onColorwaySelect,
    cssVarStyle,
    preloadImages,
  } = props;

  const themeJson = template.themeJSON;

  const isMobileOrTablet = useIsMobileOrTablet();

  const { total, totalInMinorUnits } = usePriceSummary({ templateCategory, priceUpTo: 'cardFront', cardConfig: template.themeJSON });
  const { activeProduct } = useStationeryProductPricingContext();
  const countriesWithFreeShipping = useFreeShippingCountries([activeProduct]);

  // The selected colorway is merged into the template by `Root`'s customize
  // wrapper, so this just forwards the template.
  const handleOnCustomizeNowClick = useEventCallback(() => {
    onCustomizeClick(template, totalInMinorUnits);
  });

  return (
    <PdpLayout
      themeId={themeId}
      designName={designName}
      templateCategory={templateCategory}
      isPremium={template.premium}
      isFoil={isFoil}
      onCustomizeNowClick={handleOnCustomizeNowClick}
      isCreatingDraft={isCreatingDraft}
      productSelector={productSelector}
      subtotal={total}
      showCustomizeButtonInHeader={showCustomizeButtonInHeader}
      previewPane={() => (
        <CardPreviewPane
          eventId={eventId}
          templateId={template.id}
          isFavorite={template.isFavorite}
          themeId={template.themeId}
          templateCategory={templateCategory}
          cardFront={themeJson.card.front}
          cardShape={themeJson.customizations.shape}
          thumbnailUrl={template.thumbnailUrl!}
          cardJson={template.themeJSON}
          cssVarStyle={cssVarStyle}
          selectedColorwayId={selectedColorwayId}
          preloadImages={preloadImages}
        />
      )}
      customizationPane={() => (
        <CardCustomizationPane
          hideCustomizeNowButton={isMobileOrTablet}
          intersectionRef={intersectionRef}
          customizationConfig={themeJson}
          isCreatingDraft={isCreatingDraft}
          countriesWithFreeShipping={countriesWithFreeShipping}
          templateId={template.id}
          themeId={themeId}
          themeName={designName}
          stationeryTemplateCategory={templateCategory}
          onCardCustomizeClick={handleOnCustomizeNowClick}
          onCardCustomization={onPaperCustomization}
          options={options}
          selectedColorwayId={selectedColorwayId}
          onColorwaySelect={onColorwaySelect}
        />
      )}
    />
  );
};

interface DigitalPdpProps extends BasePdpImplementationProps {
  template: DigitalDesignTemplate;
  onCustomizeDigitalClick: () => void;
  disableCustomizeNowButton?: boolean;
}

const DigitalPdp = (props: DigitalPdpProps) => {
  const {
    productSelector,
    disableCustomizeNowButton,
    intersectionRef,
    isCreatingDraft,
    onCustomizeDigitalClick,
    showCustomizeButtonInHeader,
    template,
    designName,
    templateCategory,
    isFoil,
    themeId,
  } = props;

  const isMobileOrTablet = useIsMobileOrTablet();

  return (
    <PdpLayout
      themeId={themeId}
      isFoil={isFoil}
      designName={designName}
      templateCategory={templateCategory}
      onCustomizeNowClick={onCustomizeDigitalClick}
      isCreatingDraft={isCreatingDraft}
      productSelector={productSelector}
      disableCustomizeNowButton={disableCustomizeNowButton}
      showCustomizeButtonInHeader={showCustomizeButtonInHeader}
      previewPane={() => <DigitalPreviewPane digitalDesignTemplate={template} />}
      customizationPane={() => (
        <DigitalCustomizationPane
          themeId={themeId}
          category={templateCategory}
          hideCustomizeNowButton={isMobileOrTablet}
          intersectionRef={intersectionRef}
          isCreatingDraft={isCreatingDraft}
          onCustomizeClick={onCustomizeDigitalClick}
          disableCustomizeNowButton={disableCustomizeNowButton}
        />
      )}
    />
  );
};

const productTypes: Record<string, CardFormat> = {
  stationeryCard: CardFormat.paper,
  digitalCard: CardFormat.premiumDigital,
  digital: CardFormat.digital,
};

const convertProductTypeToCardFormat = (productType: string): CardFormat => {
  return productTypes[productType] ?? CardFormat.paper;
};

const convertCardFormatToProductType = (cardFormat: CardFormat): string => {
  return Object.keys(productTypes).find(key => productTypes[key] === cardFormat) ?? 'stationeryCard';
};

const Root = (props: RootProps) => {
  const { eventId, onCustomizeCardClickOverride, template, templateCategory, digitalDesignTemplate, themeId, designName } = props;

  const supportedTemplateFormats: Format = template && digitalDesignTemplate ? 'paperAndDigital' : template ? 'paper' : 'digital';

  const queryParams = useQueryParamHelper();
  const selectedFormat = (queryParams.getValueString('format') as CardFormat | undefined) ?? CardFormat.paper;
  const initialColorwayId = queryParams.getValueString('colorway') ?? undefined;

  const { buttonInteracted } = useCardTelemetry({ page: 'productPdpModal' });

  // No printable template (digital-only theme) → basic digital is the only option.
  const hasBasicDigital = supportedTemplateFormats !== 'paper' && !template?.premium;
  const products = useMemo(() => {
    const additionalProducts = hasBasicDigital ? [FREE_DIGITAL_DUMMY_PRODUCT] : [];
    return [...(template?.products ?? []), ...additionalProducts];
  }, [hasBasicDigital, template?.products]);

  const [selectedProductId, setSelectedProductId] = useState(() => {
    const productType = convertCardFormatToProductType(selectedFormat);
    return products.find(p => p.productType === productType)?.id ?? products[0]?.id;
  });
  const selectedProductObject = products.find(p => p.id === selectedProductId);

  if (!selectedProductObject) {
    throw new Error('No product pricing found.');
  }

  const cardFormat = convertProductTypeToCardFormat(selectedProductObject.productType);

  const [storedTemplate, setStoredTemplate] = useState<StationeryTemplatePDP | null>(template);

  const {
    options,
    selectedId: selectedColorwayId,
    setSelectedId: setSelectedColorwayId,
    cssVarStyle,
    preloadImages,
  } = useColorways(storedTemplate?.themeJSON.card.front, undefined, initialColorwayId);

  // Enrich telemetry so every PDP event carries whether a suite image exists
  // for this design + colorway, enabling conversion analysis per-design.
  enrichCardTelemetryExtraInfo({ hasSuiteImage: hasSuiteImage(themeId, selectedColorwayId) });

  const history = useHistory();
  const location = useLocation();

  const handleColorwaySelect = useEventCallback((id: string) => {
    buttonInteracted({
      name: 'colorwaySwatchClicked',
      source: 'pdp',
      themeId,
      templateId: template?.id,
      stationeryTemplateCategory: templateCategory,
      colorwayId: id,
      previousColorwayId: selectedColorwayId,
      swatchIndex: options?.findIndex(option => option.id === id) ?? -1,
      totalColorways: options?.length ?? 0,
      format: cardFormat,
    });
    setSelectedColorwayId(id);
    const params = new URLSearchParams(location.search);
    params.set('colorway', id);
    history.replace({ search: params.toString() });
  });

  const colorwayControls: ColorwayControlProps = {
    options,
    selectedColorwayId,
    onColorwaySelect: handleColorwaySelect,
    cssVarStyle,
    preloadImages,
  };

  const handleOnPaperCustomization = useEventCallback((draft: DraftData) => {
    setStoredTemplate(prev =>
      prev
        ? {
            ...prev,
            themeJSON: draft.config,
          }
        : null,
    );
  });

  const currentProduct = products.find(p => p.id === selectedProductId);

  const { isCreatingDraft, onCustomizeClick } = useCustomizeClickHandler({
    currentCategory: templateCategory,
    eventId,
    onCustomizeCardClickOverride,
    supportedFormats: supportedTemplateFormats,
    currentProduct: currentProduct,
  });

  const handleOnCustomizeDigitalClick = useEventCallback(() => {
    if (digitalDesignTemplate) {
      onCustomizeClick('digital', digitalDesignTemplate, supportedTemplateFormats);
    }
  }, [template, digitalDesignTemplate]);

  const isMobileOrTablet = useIsMobileOrTablet();
  // Will be attached to the `CustomizeNowButton` in the customization pane
  // Used to determine when to show the button in the header
  const intersectionRef = useRef<HTMLButtonElement>(null);
  const shouldShowCustomizeButtonInHeader = useStickyCustomizeButton(intersectionRef, !isMobileOrTablet);

  const handleSetProductID = useEventCallback((newProductId: string) => {
    setSelectedProductId(newProductId);
    withWindow(global => {
      global.window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    });
    buttonInteracted({ name: 'pdpModal_formatControlClicked', themeId, format: newProductId });
  });

  const productSelector: StationeryProductSelector = {
    onChange: handleSetProductID,
    cardFormat: cardFormat,
    productId: selectedProductId,
  };

  const basePdpProps: BasePdpImplementationProps = {
    productSelector,
    designName,
    intersectionRef,
    isCreatingDraft,
    showCustomizeButtonInHeader: shouldShowCustomizeButtonInHeader,
    hideCustomizeButtonInCustomizationPane: isMobileOrTablet,
    templateCategory,
    themeId,
    isFoil: template ? isFoilTemplate(template) : false,
  };

  return (
    <StationeryProductPricingContextProvider
      products={products}
      activeProductId={selectedProductId}
    >
      {cardFormat === CardFormat.paper && storedTemplate && (
        <PaperPdp
          eventId={eventId}
          template={storedTemplate}
          onPaperCustomization={handleOnPaperCustomization}
          onCustomizeClick={t => onCustomizeClick('paper', withSelectedColorway(t, options, selectedColorwayId), supportedTemplateFormats)}
          {...basePdpProps}
          {...colorwayControls}
        />
      )}

      {cardFormat === CardFormat.premiumDigital && storedTemplate && (
        <PremiumDigitalPdp
          eventId={eventId}
          template={storedTemplate}
          onCustomizeClick={t => onCustomizeClick('premiumDigital', withSelectedColorway(t, options, selectedColorwayId), supportedTemplateFormats)}
          {...basePdpProps}
          {...colorwayControls}
        />
      )}

      {/* DigitalPdp renders from `digitalDesignTemplate` alone (no `storedTemplate` needed),
          so digital-only themes — which have no printable paper template — render here too. */}
      {cardFormat === CardFormat.digital && digitalDesignTemplate && (
        <DigitalPdp
          eventId={eventId}
          template={digitalDesignTemplate}
          onCustomizeDigitalClick={handleOnCustomizeDigitalClick}
          {...basePdpProps}
        />
      )}
    </StationeryProductPricingContextProvider>
  );
};

export const ProductDetailsPage = wrapWithStationeryProductDetailsHealthProvider((props: ProductDetailsPageProps) => {
  const { currentCategory } = useCardCategoryContext();
  const { themeId, templateId } = props;
  const { template, digitalTemplate, loading } = usePdpData({
    themeId,
    category: currentCategory,
    templateId,
    enableRedirectIfInvalid: true,
    enableUpdateUrlToIncludeTemplateId: true,
  });

  enrichCardTelemetryExtraInfo({ stationeryTemplateCategory: currentCategory });

  useEffect(() => {
    enrichCardTelemetryExtraInfo({ stationeryTemplateCategory: null });
  }, []);

  // Extracting design name here for consistency between pdps
  const designName = (template && templateToTitle(template)) || digitalTemplate?.name || '';

  useFreeShippingBanner(template?.products ?? []);

  return (
    <>
      <PdpSeo
        themeId={themeId}
        currentCategory={currentCategory}
      />
      <SkeletonGroup
        // Ready when a printable template has products, OR a digital-only theme (no
        // printable template at all) has a digital design. A template that IS present but
        // has empty products stays in the skeleton rather than mounting Root into a
        // productless state (which would throw or silently hide the paper format).
        isReady={!loading && (!!template?.products.length || (!template && !!digitalTemplate))}
        placeholder={<PdpLayout.Skeleton />}
        width="100%"
      >
        {template || digitalTemplate ? (
          <Root
            key={templateId}
            template={template}
            digitalDesignTemplate={digitalTemplate}
            designName={designName}
            templateCategory={currentCategory}
            {...props}
          />
        ) : null}
      </SkeletonGroup>
    </>
  );
});
